import React, { useState } from 'react';
import { Card, Typography, Tabs, Collapse, Tag, Space, Button, message, Alert } from 'antd';
import { CopyOutlined, ApiOutlined, KeyOutlined, SyncOutlined, UserAddOutlined, DollarOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../config/environment';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const APIDocumentationPage: React.FC = () => {
  const baseUrl = API_BASE_URL;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard!');
  };

  const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'bash' }) => (
    <div style={{ position: 'relative' }}>
      <Button
        icon={<CopyOutlined />}
        size="small"
        style={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        onClick={() => copyToClipboard(code)}
      >
        Copy
      </Button>
      <pre style={{
        background: '#f5f5f5',
        padding: '16px',
        borderRadius: '4px',
        overflow: 'auto',
        fontSize: '13px'
      }}>
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <ApiOutlined /> API Documentation
      </Title>
      <Paragraph>
        Complete API documentation for integrating Kiyzer.ai/Odoo ERP with Texo OCR Platform.
        All ERP API endpoints require authentication using an API key generated from the ERP API Keys page.
      </Paragraph>

      <Alert
        message="Authentication Required"
        description="All ERP API requests must include your API key in the X-API-Key header."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Tabs defaultActiveKey="authentication">
        <TabPane tab={<span><KeyOutlined /> Authentication</span>} key="authentication">
          <Card>
            <Title level={4}>API Key Authentication</Title>
            <Paragraph>
              Generate an API key from the <strong>ERP API Keys</strong> page. Include it in all requests:
            </Paragraph>
            
            <CodeBlock code={`curl -H "X-API-Key: texo_erp_abc123..." ${baseUrl}/api/erp/...`} />

            <Alert
              message="Security Note"
              description="Keep your API keys secure. The full key is only shown once during generation. Store it safely in your environment variables or secrets manager."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><UserAddOutlined /> Tenant Onboarding</span>} key="tenant">
          <Collapse defaultActiveKey={['create-tenant']} accordion>
            <Panel header="POST /api/erp/tenants/create - Create New Tenant" key="create-tenant">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Create a new tenant (customer) when they purchase a subscription through Kiyzer.ai.
                    This creates the tenant account, admin user, and allocates initial credits.
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Endpoint:</Text>
                  <CodeBlock code={`POST ${baseUrl}/api/erp/tenants/create`} />
                </div>

                <div>
                  <Text strong>Request Headers:</Text>
                  <CodeBlock code={`X-API-Key: your_erp_api_key_here`} />
                </div>

                <div>
                  <Text strong>Request Body:</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "email": "customer@example.com",
  "company_name": "Acme Corporation",
  "contact_name": "John Doe",
  "phone": "+1234567890",
  "plan_code": "PROFESSIONAL",
  "credits": 5000
}`}
                  />
                </div>

                <div>
                  <Text strong>Request Fields:</Text>
                  <ul>
                    <li><Tag color="red">email</Tag> (required) - Customer's email address</li>
                    <li><Tag color="red">company_name</Tag> (required) - Company/organization name</li>
                    <li><Tag color="red">contact_name</Tag> (required) - Primary contact person name</li>
                    <li><Tag color="red">plan_code</Tag> (required) - Plan code: BASIC, PROFESSIONAL, ENTERPRISE, or CUSTOM</li>
                    <li><Tag color="red">credits</Tag> (required) - Initial credits to allocate</li>
                    <li><Tag color="blue">phone</Tag> (optional) - Contact phone number</li>
                  </ul>
                </div>

                <div>
                  <Text strong>Success Response (201):</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "tenant_id": "TEN-ABC12345",
  "admin_user_id": "user_xyz789abc",
  "status": "active",
  "plan_code": "PROFESSIONAL",
  "credits_allocated": 5000,
  "password_reset_token": "token_abc123...",
  "password_reset_url": "https://app.texo.com/reset-password?token=...",
  "message": "Tenant created successfully"
}`}
                  />
                </div>

                <div>
                  <Text strong>Error Responses:</Text>
                  <Paragraph>
                    <Tag color="orange">400</Tag> Missing required fields or invalid plan code<br />
                    <Tag color="orange">401</Tag> Invalid or missing API key<br />
                    <Tag color="orange">409</Tag> Email already exists<br />
                    <Tag color="red">500</Tag> Server error
                  </Paragraph>
                </div>

                <div>
                  <Text strong>cURL Example:</Text>
                  <CodeBlock
                    code={`curl -X POST ${baseUrl}/api/erp/tenants/create \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: texo_erp_your_key_here" \\
  -d '{
    "email": "customer@example.com",
    "company_name": "Acme Corp",
    "contact_name": "John Doe",
    "phone": "+1234567890",
    "plan_code": "PROFESSIONAL",
    "credits": 5000
  }'`}
                  />
                </div>
              </Space>
            </Panel>

            <Panel header="GET /api/erp/tenants/{tenant_id} - Get Tenant Details" key="get-tenant">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>Retrieve detailed information about a specific tenant.</Paragraph>
                </div>

                <div>
                  <Text strong>Endpoint:</Text>
                  <CodeBlock code={`GET ${baseUrl}/api/erp/tenants/TEN-ABC12345`} />
                </div>

                <div>
                  <Text strong>Success Response (200):</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "tenant_id": "TEN-ABC12345",
  "company_name": "Acme Corporation",
  "email": "customer@example.com",
  "phone": "+1234567890",
  "status": "active",
  "plan_code": "PROFESSIONAL",
  "plan_name": "Professional Plan",
  "credit_balance": 3450,
  "total_purchased": 5000,
  "total_consumed": 1550,
  "last_topup_date": "2026-01-15T10:30:00Z",
  "users_count": 3,
  "created_at": "2026-01-01T08:00:00Z",
  "created_via": "erp"
}`}
                  />
                </div>
              </Space>
            </Panel>
          </Collapse>
        </TabPane>

        <TabPane tab={<span><DollarOutlined /> Credit Management</span>} key="credits">
          <Collapse defaultActiveKey={['topup']} accordion>
            <Panel header="POST /api/erp/tenants/{tenant_id}/credits/topup - Top-up Credits" key="topup">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Add credits to a tenant's account after they make a purchase through Kiyzer.ai.
                    This should be called after payment is confirmed.
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Endpoint:</Text>
                  <CodeBlock code={`POST ${baseUrl}/api/erp/tenants/TEN-ABC12345/credits/topup`} />
                </div>

                <div>
                  <Text strong>Request Body:</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "credits": 5000,
  "transaction_reference": "PURCHASE-202602031234",
  "payment_amount": 40.00,
  "currency": "USD",
  "plan_code": "PROFESSIONAL"
}`}
                  />
                </div>

                <div>
                  <Text strong>Request Fields:</Text>
                  <ul>
                    <li><Tag color="red">credits</Tag> (required) - Number of credits to add</li>
                    <li><Tag color="blue">transaction_reference</Tag> (optional) - Your payment/order reference ID</li>
                    <li><Tag color="blue">payment_amount</Tag> (optional) - Amount paid in currency</li>
                    <li><Tag color="blue">currency</Tag> (optional) - Currency code (default: USD)</li>
                    <li><Tag color="blue">plan_code</Tag> (optional) - Plan code for this purchase</li>
                  </ul>
                </div>

                <div>
                  <Text strong>Success Response (200):</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "tenant_id": "TEN-ABC12345",
  "transaction_id": "PURCHASE-202602031234",
  "credits_added": 5000,
  "balance_before": 450,
  "balance_after": 5450,
  "timestamp": null
}`}
                  />
                </div>

                <div>
                  <Text strong>cURL Example:</Text>
                  <CodeBlock
                    code={`curl -X POST ${baseUrl}/api/erp/tenants/TEN-ABC12345/credits/topup \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: texo_erp_your_key_here" \\
  -d '{
    "credits": 5000,
    "transaction_reference": "ORDER-202602031234",
    "payment_amount": 40.00,
    "currency": "USD",
    "plan_code": "PROFESSIONAL"
  }'`}
                  />
                </div>
              </Space>
            </Panel>

            <Panel header="GET /api/erp/tenants/{tenant_id}/usage - Get Tenant Usage" key="usage">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>Retrieve credit usage statistics for a tenant within a date range.</Paragraph>
                </div>

                <div>
                  <Text strong>Endpoint:</Text>
                  <CodeBlock code={`GET ${baseUrl}/api/erp/tenants/TEN-ABC12345/usage?from=2026-02-01&to=2026-02-28`} />
                </div>

                <div>
                  <Text strong>Query Parameters:</Text>
                  <ul>
                    <li><Tag color="blue">from</Tag> (optional) - Start date (YYYY-MM-DD), defaults to current month start</li>
                    <li><Tag color="blue">to</Tag> (optional) - End date (YYYY-MM-DD), defaults to current date</li>
                  </ul>
                </div>

                <div>
                  <Text strong>Success Response (200):</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "tenant_id": "TEN-ABC12345",
  "company_name": "Acme Corporation",
  "period": {
    "from": "2026-02-01",
    "to": "2026-02-28"
  },
  "total_requests": 1550,
  "credits_consumed": 1550,
  "current_balance": 3450,
  "total_purchased": 5000,
  "total_consumed": 1550
}`}
                  />
                </div>
              </Space>
            </Panel>
          </Collapse>
        </TabPane>

        <TabPane tab={<span><SyncOutlined /> Nightly Sync</span>} key="sync">
          <Collapse defaultActiveKey={['balances']} accordion>
            <Panel header="GET /api/erp/tenants/credit-balances - Get All Credit Balances" key="balances">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Retrieve credit balances and usage statistics for all tenants. 
                    Use this endpoint for nightly synchronization with Kiyzer.ai to update billing records.
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Endpoint:</Text>
                  <CodeBlock code={`GET ${baseUrl}/api/erp/tenants/credit-balances`} />
                </div>

                <div>
                  <Text strong>Query Parameters:</Text>
                  <ul>
                    <li><Tag color="blue">date</Tag> (optional) - Filter by date (YYYY-MM-DD)</li>
                    <li><Tag color="blue">status</Tag> (optional) - Filter by tenant status (active, suspended)</li>
                  </ul>
                </div>

                <div>
                  <Text strong>Success Response (200):</Text>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "date": "2026-02-03",
  "total_tenants": 25,
  "tenants": [
    {
      "tenant_id": "TEN-ABC12345",
      "company_name": "Acme Corporation",
      "email": "customer@example.com",
      "status": "active",
      "credit_balance": 3450,
      "total_purchased": 5000,
      "total_consumed": 1550,
      "credits_consumed_today": 45,
      "credits_consumed_month": 1550,
      "last_topup_date": "2026-01-15T10:30:00Z",
      "plan_code": "PROFESSIONAL",
      "thresholds_triggered": ["50"]
    },
    {
      "tenant_id": "TEN-XYZ67890",
      "company_name": "Beta Industries",
      "email": "admin@beta.com",
      "status": "active",
      "credit_balance": 850,
      "total_purchased": 1000,
      "total_consumed": 150,
      "credits_consumed_today": 12,
      "credits_consumed_month": 150,
      "last_topup_date": "2026-02-01T14:20:00Z",
      "plan_code": "BASIC",
      "thresholds_triggered": []
    }
  ]
}`}
                  />
                </div>

                <div>
                  <Text strong>Usage Recommendation:</Text>
                  <Alert
                    message="Nightly Sync Strategy"
                    description={
                      <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        <li>Schedule this API call daily at midnight (00:00 UTC)</li>
                        <li>Store the data in your Odoo database for billing and reporting</li>
                        <li>Monitor thresholds_triggered array to identify low-balance customers</li>
                        <li>Trigger automated emails or alerts for customers needing top-ups</li>
                        <li>Use credits_consumed_month for monthly billing reconciliation</li>
                      </ul>
                    }
                    type="success"
                    showIcon
                  />
                </div>

                <div>
                  <Text strong>Python Example (for Odoo integration):</Text>
                  <CodeBlock
                    language="python"
                    code={`import requests
from datetime import datetime

def sync_texo_credit_balances():
    """Nightly sync of Texo OCR credit balances to Odoo"""
    api_key = "texo_erp_your_key_here"
    url = "${baseUrl}/api/erp/tenants/credit-balances"
    
    headers = {
        "X-API-Key": api_key
    }
    
    response = requests.get(url, headers=headers)
    
    if response.status_code == 200:
        data = response.json()
        print(f"✅ Synced {data['total_tenants']} tenants")
        
        for tenant in data['tenants']:
            # Update Odoo customer record
            update_odoo_customer(
                customer_id=tenant['tenant_id'],
                credit_balance=tenant['credit_balance'],
                consumed_month=tenant['credits_consumed_month']
            )
            
            # Alert if low balance
            if tenant['credit_balance'] < 100:
                send_low_balance_alert(tenant)
    else:
        print(f"❌ Sync failed: {response.status_code}")

# Schedule this function to run daily at midnight
sync_texo_credit_balances()`}
                  />
                </div>
              </Space>
            </Panel>
          </Collapse>
        </TabPane>

        <TabPane tab="Error Codes" key="errors">
          <Card>
            <Title level={4}>Common Error Codes</Title>
            <Paragraph>All error responses follow this format:</Paragraph>
            <CodeBlock
              language="json"
              code={`{
  "success": false,
  "error_code": "ERROR_CODE_HERE",
  "message": "Human readable error message"
}`}
            />

            <Title level={5} style={{ marginTop: 24 }}>Error Code Reference:</Title>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>HTTP</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>MISSING_API_KEY</Tag></td>
                  <td style={{ padding: '12px' }}>401</td>
                  <td style={{ padding: '12px' }}>X-API-Key header not provided</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>INVALID_API_KEY</Tag></td>
                  <td style={{ padding: '12px' }}>401</td>
                  <td style={{ padding: '12px' }}>API key is invalid or has been revoked</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>MISSING_FIELD</Tag></td>
                  <td style={{ padding: '12px' }}>400</td>
                  <td style={{ padding: '12px' }}>Required field missing from request body</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>INVALID_PLAN_CODE</Tag></td>
                  <td style={{ padding: '12px' }}>400</td>
                  <td style={{ padding: '12px' }}>Plan code not recognized (use BASIC, PROFESSIONAL, ENTERPRISE, CUSTOM)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>EMAIL_EXISTS</Tag></td>
                  <td style={{ padding: '12px' }}>409</td>
                  <td style={{ padding: '12px' }}>Email already registered in system</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>TENANT_NOT_FOUND</Tag></td>
                  <td style={{ padding: '12px' }}>404</td>
                  <td style={{ padding: '12px' }}>Tenant ID does not exist</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>MISSING_CREDITS</Tag></td>
                  <td style={{ padding: '12px' }}>400</td>
                  <td style={{ padding: '12px' }}>Credits field required in request body</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}><Tag>INVALID_REQUEST</Tag></td>
                  <td style={{ padding: '12px' }}>400</td>
                  <td style={{ padding: '12px' }}>Request body validation failed</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px' }}><Tag>SERVER_ERROR</Tag></td>
                  <td style={{ padding: '12px' }}>500</td>
                  <td style={{ padding: '12px' }}>Internal server error (contact support)</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </TabPane>

        <TabPane tab="Rate Limits" key="limits">
          <Card>
            <Title level={4}>Rate Limits</Title>
            <Paragraph>
              ERP API endpoints have the following rate limits to ensure system stability:
            </Paragraph>

            <ul>
              <li><strong>Tenant Creation:</strong> 100 requests per hour</li>
              <li><strong>Credit Top-up:</strong> 500 requests per hour</li>
              <li><strong>Nightly Sync (credit-balances):</strong> 10 requests per hour</li>
              <li><strong>Usage Queries:</strong> 1000 requests per hour</li>
            </ul>

            <Alert
              message="Rate Limit Headers"
              description={
                <div>
                  Response headers include rate limit information:
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    <li><code>X-RateLimit-Limit</code> - Maximum requests per window</li>
                    <li><code>X-RateLimit-Remaining</code> - Remaining requests in window</li>
                    <li><code>X-RateLimit-Reset</code> - Unix timestamp when limit resets</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />

            <Alert
              message="429 Too Many Requests"
              description="If you exceed rate limits, you'll receive a 429 status code. Implement exponential backoff in your integration."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default APIDocumentationPage;
